import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { LayoutDashboard, Users, CalendarDays, LogOut, Loader2, Lock } from "lucide-react";
import { Logo } from "../components/Navbar";
import { adminLogin, setAdminToken, getAdminToken, clearAdminToken } from "../lib/api";
import Dashboard from "../components/admin/Dashboard";
import LeadsView from "../components/admin/LeadsView";
import BatchesView from "../components/admin/BatchesView";

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: Users },
  { id: "batches", label: "Batches", icon: CalendarDays },
];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { token } = await adminLogin(email, password);
      setAdminToken(token);
      onLogin(token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070A08] flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-sm rounded-2xl bg-[#0F1712] border border-[#22C55E]/[0.18] p-8 text-center shadow-[0_0_60px_-20px_rgba(34,197,94,0.3)]">
        <div className="mx-auto w-12 h-12 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/30 flex items-center justify-center">
          <Lock className="w-5 h-5 text-[#22C55E]" />
        </div>
        <h1 className="mt-4 font-heading uppercase text-3xl text-white tracking-wide" data-testid="admin-login-title">Marubazu CRM</h1>
        <p className="mt-1.5 text-sm text-[#6B7A70]">Sign in to manage your leads</p>
        <form onSubmit={submit} className="mt-6 space-y-3.5">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            data-testid="admin-email-input"
            className="w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-4 py-3 text-white text-sm placeholder:text-[#6B7A70] transition-colors"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            data-testid="admin-password-input"
            className="w-full rounded-xl bg-[#070A08] border border-[#22C55E]/[0.18] focus:border-[#22C55E]/60 outline-none px-4 py-3 text-white text-sm placeholder:text-[#6B7A70] transition-colors"
          />
          {error && <p className="text-sm text-[#EF4444]" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-60 text-[#04100A] font-extrabold text-sm px-6 py-3.5 transition-colors"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Log in
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => getAdminToken());
  const [view, setView] = useState("dashboard");

  useEffect(() => {
    const onLogout = () => setToken("");
    window.addEventListener("admin:logout", onLogout);
    return () => window.removeEventListener("admin:logout", onLogout);
  }, []);

  if (!token) return <Login onLogin={setToken} />;

  const logout = () => {
    clearAdminToken();
    setToken("");
  };

  return (
    <div className="min-h-screen bg-[#070A08] text-white" data-testid="admin-page">
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#0F1712", border: "1px solid rgba(34,197,94,0.3)", color: "#fff" } }} />
      <header className="sticky top-0 z-40 border-b border-[#17231B] bg-[#070A08]/90 backdrop-blur-md">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Logo size="text-xl" />
          <nav className="flex items-center gap-1 sm:gap-2" aria-label="Admin">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setView(id)}
                data-testid={`admin-tab-${id}`}
                className={`inline-flex items-center gap-2 rounded-full px-3.5 sm:px-4 py-2 text-sm font-semibold transition-colors ${
                  view === id ? "bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40" : "text-[#A7B3AA] hover:text-white border border-transparent"
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </nav>
          <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-[#A7B3AA] hover:text-white transition-colors">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Log out</span>
          </button>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {view === "dashboard" && <Dashboard />}
        {view === "leads" && <LeadsView />}
        {view === "batches" && <BatchesView />}
      </main>
    </div>
  );
}
